import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

export interface Review {
  id: string;
  user_id: string;
  tool_slug: string;
  rating: number;
  title: string;
  content: string | null;
  created_at: string;
  updated_at: string;
  profile?: { display_name: string | null; avatar_url: string | null };
}

export const useReviews = (toolSlug: string) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("tool_reviews")
      .select("*")
      .eq("tool_slug", toolSlug)
      .order("created_at", { ascending: false });

    if (!error && data) {
      // Fetch profiles for reviewers
      const userIds = [...new Set(data.map((r) => r.user_id))];
      const { data: profiles } = await supabase
        // public_profiles view excludes phone / phone_verified_at and is
        // the only profile surface anon/authenticated should hit for
        // display lookups (RLS on profiles is now owner-only).
        .from("public_profiles")
        .select("user_id, display_name, avatar_url")
        .in("user_id", userIds);

      const profileMap = new Map(
        (profiles || []).map((p) => [p.user_id, p])
      );

      setReviews(
        data.map((r) => ({
          ...r,
          profile: profileMap.get(r.user_id) || undefined,
        }))
      );
    }
    setLoading(false);
  }, [toolSlug]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const submitReview = useCallback(
    async (rating: number, title: string, content: string) => {
      if (!user) {
        toast.error("Sign in to leave a review");
        return false;
      }
      setSubmitting(true);

      // Check if user already reviewed
      const existing = reviews.find((r) => r.user_id === user.id);
      let error;

      if (existing) {
        ({ error } = await supabase
          .from("tool_reviews")
          .update({ rating, title: title.slice(0, 200), content: content.slice(0, 2000) })
          .eq("id", existing.id));
      } else {
        ({ error } = await supabase
          .from("tool_reviews")
          .insert({
            user_id: user.id,
            tool_slug: toolSlug,
            rating,
            title: title.slice(0, 200),
            content: content.slice(0, 2000),
          }));
      }

      setSubmitting(false);
      if (error) {
        toast.error("Failed to submit review");
        return false;
      }
      toast.success(existing ? "Review updated!" : "Review submitted!");
      await fetchReviews();
      return true;
    },
    [user, toolSlug, reviews, fetchReviews]
  );

  const deleteReview = useCallback(
    async (reviewId: string) => {
      const { error } = await supabase
        .from("tool_reviews")
        .delete()
        .eq("id", reviewId);
      if (error) {
        toast.error("Failed to delete review");
      } else {
        toast.success("Review deleted");
        setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      }
    },
    []
  );

  const averageRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0;

  const userReview = user
    ? reviews.find((r) => r.user_id === user.id)
    : undefined;

  return {
    reviews,
    loading,
    submitting,
    submitReview,
    deleteReview,
    averageRating,
    userReview,
  };
};
